/**
 * Nome de usuário como identidade de login.
 *
 * O Supabase Auth só autentica por e-mail ou telefone — não existe
 * identificador "usuário" nativo. A saída é um e-mail SINTÉTICO: quem digita
 * `maria` entra como `maria@fiv.invalid`. O endereço nunca é mostrado, nunca
 * recebe mensagem e não precisa existir; ele é só o formato que o GoTrue
 * aceita para guardar o identificador.
 *
 * A conversão é determinística, feita aqui no cliente. A alternativa — uma
 * tabela ou função que traduz usuário → e-mail — exigiria uma consulta
 * deslogado (portanto aberta ao anônimo) que confirmaria quais usuários
 * existem e devolveria o e-mail de cada um. Calcular dispensa a consulta e não
 * revela nada.
 *
 * `.invalid` é reservado pela RFC 2606 e nunca resolve. É a escolha que torna
 * IMPOSSÍVEL uma mensagem sair para um domínio de terceiros por engano — algo
 * que um `@empresa.com.br` inventado não garante. Se um dia a validação estrita
 * de e-mail for ligada no painel do Supabase (checagem de MX), este é o ponto
 * único a trocar por um domínio real.
 */

export const DOMINIO_INTERNO = 'fiv.invalid';

/**
 * Forma canônica do nome de usuário.
 *
 * Precisa ser estável nas duas pontas: o admin cadastra "Maria Silva" e a
 * pessoa digita "maria silva" no login — as duas têm que chegar no mesmo
 * endereço. Daí tirar acento, baixar a caixa e reduzir o resto a ponto.
 */
export const normalizarUsuario = (texto = '') =>
  String(texto)
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '.')
    .replace(/^\.+|\.+$/g, '');

/** Endereço interno correspondente ao nome de usuário. */
export const emailDeUsuario = (usuario) => `${normalizarUsuario(usuario)}@${DOMINIO_INTERNO}`;

/**
 * O que o campo de login envia ao Supabase.
 *
 * Aceita e-mail de verdade também: as contas criadas antes desta mudança têm
 * endereço real no Auth e continuam entrando com ele. Sem isso a troca
 * trancaria para fora todo mundo que já usa o sistema.
 */
export const identificadorParaEmail = (entrada = '') => {
  const texto = String(entrada).trim();
  return texto.includes('@') ? texto.toLowerCase() : emailDeUsuario(texto);
};

/**
 * O que a tela mostra: o usuário, não o endereço sintético.
 *
 * Endereço real passa inteiro — é dado verdadeiro de conta antiga e esconder a
 * parte do domínio faria dois usuários diferentes parecerem o mesmo.
 */
export const usuarioDeEmail = (email) => {
  if (!email) return '';
  const sufixo = `@${DOMINIO_INTERNO}`;
  return email.endsWith(sufixo) ? email.slice(0, -sufixo.length) : email;
};

/** Rótulo de identificação de uma pessoa, na ordem em que a tela prefere. */
export const nomeExibido = (perfil, usuario) =>
  perfil?.full_name || usuarioDeEmail(perfil?.email || usuario?.email) || 'Sem nome';
